import React, { useEffect, useMemo, useState } from 'react';
import { Input, Table, DOMHelper, Stack, IconButton, Divider, SelectPicker, Tag } from 'rsuite';
import type { SortType } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import {
  ClubEventParticipation,
  ClubEventParticipantsPaginationFilterRequest,
  ClubEventParticipationStatusEnum,
  ClubEventParticipationStatusList,
} from '@/models/running-club-event-participation.model';
import { getPagedRunningClubEventParticipants } from '@/services/running-club-event.service';
import PageContent from '@/components/PageContent';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface RunningClubEventParticipantListProps {
  runningClubEventId: number;
}
const RunningClubEventParticipantList : React.FC<RunningClubEventParticipantListProps> = ({runningClubEventId}) => {
  const [filterReq, setFilterReq] = useState<ClubEventParticipantsPaginationFilterRequest>(new ClubEventParticipantsPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<ClubEventParticipation[]>([]);
  const [loading, setLoading] = useState(true);
  // Client-side only: the Api's participants filter accepts just runningClubEventId, so this
  // narrows the rows already loaded on the current page — it does not re-query the server.
  const [statusFilter, setStatusFilter] = useState<ClubEventParticipationStatusEnum | null>(null);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ClubEventParticipantsPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubEventId
      };
      const result: PagedResponseModel<ClubEventParticipation> = await getPagedRunningClubEventParticipants(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching club event participants:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  // Surface pending check-ins first: sort them to the top of the current page so an admin
  // opening the tab immediately sees whether anything needs the club staff's attention.
  const displayedItems = useMemo(() => {
    const filtered = statusFilter
      ? items.filter(itm => itm.status === statusFilter)
      : items;
    return [...filtered].sort((a, b) => {
      const aPending = a.status === ClubEventParticipationStatusEnum.CheckInPending ? 0 : 1;
      const bPending = b.status === ClubEventParticipationStatusEnum.CheckInPending ? 0 : 1;
      return aPending - bPending;
    });
  }, [items, statusFilter]);

  const pendingCount = useMemo(
    () => items.filter(itm => itm.status === ClubEventParticipationStatusEnum.CheckInPending).length,
    [items]
  );

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))}
          />
          <SelectPicker
            placeholder="Status"
            data={ClubEventParticipationStatusList}
            value={statusFilter}
            onChange={value => setStatusFilter(value as ClubEventParticipationStatusEnum | null)}
            style={{ width: 200 }}
            cleanable
          />
        </Stack>
        {pendingCount > 0 && (
          <Tag color="orange">{pendingCount} check-in{pendingCount > 1 ? 's' : ''} pending approval</Tag>
        )}
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={displayedItems}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Profile</HeaderCell>
          <ImageCell dataKey="user.profilePicture" />
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Username</HeaderCell>
          <Cell>{(rowData: ClubEventParticipation) => rowData.user.username}</Cell>
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>First Name</HeaderCell>
          <Cell>{(rowData: ClubEventParticipation) => rowData.user.firstName}</Cell>
        </Column>
        <Column flexGrow={1} align="left" fullText>
          <HeaderCell>Last Name</HeaderCell>
          <Cell>{(rowData: ClubEventParticipation) => rowData.user.lastName}</Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText>
          <HeaderCell>Membership</HeaderCell>
          <Cell>
            {(rowData: ClubEventParticipation) => {
              if (rowData.isClubMember === false) return <Tag color="violet">Non-member</Tag>;
              if (rowData.isClubMember === true) return 'Member';
              return '-';
            }}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status">
            {(rowData: ClubEventParticipation) => {
              const label = ClubEventParticipationStatusList.find(s => s.value === rowData.status)?.label ?? 'Unknown';
              const isPending = rowData.status === ClubEventParticipationStatusEnum.CheckInPending;
              return isPending ? <Tag color="orange">{label}</Tag> : label;
            }}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Joined at</HeaderCell>
          <Cell dataKey="joinedAt">
            {(rowData: ClubEventParticipation) => jsDateToZone(rowData.joinedAt)}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Checked in at</HeaderCell>
          <Cell dataKey="checkedInAt">
            {(rowData: ClubEventParticipation) => jsDateToZone(rowData.checkedInAt)}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right">
        <HeaderCell>...</HeaderCell>
        <Cell style={{ padding: '6px' }}>
          {(rowData: ClubEventParticipation) => <IconButton onClick={() => nav(`/users/${rowData.user.id}`)} icon={<EditIcon />} circle size="sm" /> }
        </Cell>
      </Column>

      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

    </PageContent>
  );
};

export default RunningClubEventParticipantList;
